// Stripe Checkout success landing page.
//
// User lands here after completing payment on Stripe's hosted Checkout. The
// `session_id` query param is filled in by Stripe via the {CHECKOUT_SESSION_ID}
// placeholder we set on success_url when creating the session.
//
// IMPORTANT — this page is "thank you" UX only. The actual plan + credit
// update in Supabase happens asynchronously via the Stripe webhook (next
// phase). When the webhook fires, useCredits.refresh() picks up the new
// values. For now (pre-webhook), the user's account state in Supabase will
// NOT change after a successful payment — that's intentional, we're shipping
// the checkout flow in isolation first.

import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCredits } from '@/hooks/useCredits';
import { loadWizardSnapshot } from '@/lib/wizardSnapshot';
import { trackPixel } from '@/lib/metaPixel';
import { track } from '@/lib/analytics';

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refresh: refreshCredits, planTier, trialEndsAt, loaded } = useCredits();

  // Best-effort refresh of credits/plan in case the webhook already fired by
  // the time the user got redirected here. Cheap call — runs once on mount.
  useEffect(() => {
    refreshCredits();
  }, [refreshCredits]);

  // The Stripe webhook that grants the plan/trial is ASYNC — it can land a few
  // seconds AFTER Stripe redirects here. We must NOT show the "Payment
  // successful" choices (or route to /create) until the grant has actually
  // landed, or /create's AuthGate plan-gate bounces the just-paid user back to
  // /checkout. `planReady` is the SAME signal AuthGate trusts to admit a user:
  // a confirmed paid tier OR an active free-trial.
  const planReady =
    loaded &&
    (planTier !== 'free' ||
      (!!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now()));

  // Re-fetch the profile until that signal flips true. This is a readiness
  // CHECK, not a timed wait — the success modal is revealed strictly when the
  // grant is confirmed, never on a guessed delay. The interval clears itself
  // the moment planReady becomes true (effect re-runs → early return).
  useEffect(() => {
    if (planReady) return;
    const id = setInterval(() => { void refreshCredits(); }, 2000);
    return () => clearInterval(id);
  }, [planReady, refreshCredits]);

  // Freemium: every checkout that lands here is a real paid subscription
  // (charged today, no trial), so fire the Meta Pixel Subscribe conversion
  // (ref-guarded so it fires exactly once, never on a re-render / StrictMode
  // double-invoke). NB: Meta ad campaigns must optimize for `Subscribe`.
  const subscribeTracked = useRef(false);
  useEffect(() => {
    if (subscribeTracked.current) return;
    subscribeTracked.current = true;
    trackPixel('Subscribe');
    track('subscription_started');
  }, []);

  // If the user was mid-wizard when they went to checkout, the snapshot
  // module preserved their state. Surface a "Continue your post" CTA so
  // they know their work survived. Snapshot is fresh-checked (TTL 30
  // min) so this won't show for users who landed here unrelated to a
  // wizard session.
  const hasPendingWizard = !!loadWizardSnapshot();

  // Until the grant is CONFIRMED, show a finalizing state — never the success
  // choices. Seeing the modal below is the guarantee everything is in place, so
  // "Start creating" can't bounce off /create's plan-gate.
  if (!planReady) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-surface-dark/40 shadow-lg p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-5 rounded-full border-[3px] border-primary/25 border-t-primary animate-spin" />
          <h1 className="font-display font-bold text-dark text-xl mb-2">
            Finalizing your account…
          </h1>
          <p className="text-dark-muted text-sm">
            Payment received. We&apos;re activating your subscription and adding your
            credits — this only takes a moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-surface-dark/40 shadow-lg p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
          <i className="ri-check-line text-emerald-600 text-3xl" />
        </div>

        <h1 className="font-display font-bold text-dark text-2xl mb-2">
          Payment successful
        </h1>
        <p className="text-dark-muted text-sm mb-6">
          Thanks for upgrading. Your subscription is now active in Stripe; your
          credits will appear in your account within a minute or two.
        </p>

        <div className="flex flex-col gap-2">
          <button
            // If a wizard snapshot exists, the restoration logic lives in
            // /app's page.tsx — route there to resume mid-flow. Otherwise
            // send the user to the dashboard (/create) which is the new
            // default entry point. (The snapshot path will move to /appv2
            // when /app is retired; until then, keep this fork.)
            onClick={() => navigate(hasPendingWizard ? '/app' : '/create')}
            className="w-full px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer"
          >
            {hasPendingWizard ? 'Continue your post' : 'Start creating'}
          </button>
          <button
            onClick={() => navigate('/auth/profile')}
            className="w-full px-6 py-3 rounded-full bg-surface text-dark text-sm font-semibold hover:bg-surface-dark/20 transition-colors cursor-pointer"
          >
            View account
          </button>
        </div>

        {sessionId && (
          <p className="text-dark-muted text-[10px] mt-6 break-all">
            Reference: {sessionId}
          </p>
        )}
      </div>
    </div>
  );
}
